/**
 * Custom Subscription Widget
 * Handles product subscription options and dynamic price updates
 */

if (!customElements.get('custom-subscription-widget')) {
  class CustomSubscriptionWidget extends HTMLElement {
    // Constants for selectors and attributes
    static SELECTORS = {
      INPUT: '[js-subscription-widget-input]',
      SELLING_PLAN: '[js-selling-plan]',
      PRODUCT_PRICE: '[js-cs-product-price]',
      PRODUCT_DETAILS: '.Product_inner_new',
      VARIANT_INPUT: 'product-form-component input[name="id"]',
      SUBSCRIBE_LABEL: 'label[for="subscribe-input"]',
    };

    static EVENTS = {
      INIT: 'subscriptionWidget:init',
      CHANGE: 'change',
    };

    static ATTRIBUTES = {
      FORM_ID: 'data-form-id',
      SECTION_ID: 'data-section-id',
      PRODUCT_HANDLE: 'data-product-handle',
    };

    static SUBSCRIPTION_TYPE = {
      SUBSCRIPTION: 'subscription',
      ONE_TIME: 'one-time',
    };

    static CSS_CLASSES = {
      LOADING: 'loading',
    };

    constructor() {
      super();
      this.initializeElements();
      this.setupEventListeners();
    }

    /**
     * Initialize DOM elements and component properties
     * @private
     */
    initializeElements() {
      this.inputs = this.querySelectorAll(CustomSubscriptionWidget.SELECTORS.INPUT);
      this.sellingPlanInputs = this.querySelectorAll(CustomSubscriptionWidget.SELECTORS.SELLING_PLAN);
      this.formId = this.getAttribute(CustomSubscriptionWidget.ATTRIBUTES.FORM_ID);
      this.sectionId = this.getAttribute(CustomSubscriptionWidget.ATTRIBUTES.SECTION_ID);
      this.productHandle = this.getAttribute(CustomSubscriptionWidget.ATTRIBUTES.PRODUCT_HANDLE);
    }

    /**
     * Setup custom event listeners
     * @private
     */
    setupEventListeners() {
      this.addEventListener(CustomSubscriptionWidget.EVENTS.INIT, this.handleInit.bind(this));
    }

    /**
     * Handle initialization event
     * @private
     */
    handleInit() {
      const checkedInput = this.querySelector(`${CustomSubscriptionWidget.SELECTORS.INPUT}:checked`);
      if (!checkedInput || !(checkedInput instanceof HTMLInputElement)) {
        console.warn('No subscription input is checked');
        return;
      }

      const type = checkedInput.value;
      this.setPriceOpacity(0);

      const url = this.createUrl(type);
      this.render(url);
    }

    /**
     * Called when element is connected to the DOM
     */
    connectedCallback() {
      this.attachInputListeners();
      this.attachSellingPlanListeners();
    }

    /**
     * Attach change listeners to subscription type inputs
     * @private
     */
    attachInputListeners() {
      if (!this.inputs) return;

      this.inputs.forEach((input) => {
        input.addEventListener(CustomSubscriptionWidget.EVENTS.CHANGE, (event) => {
          const target = event.target;
          if (!target || !(target instanceof HTMLInputElement)) return;

          const type = target.value;
          const url = this.createUrl(type);
          this.render(url);
        });
      });
    }

    /**
     * Attach change listeners to selling plan inputs
     * @private
     */
    attachSellingPlanListeners() {
      if (!this.sellingPlanInputs) return;

      this.sellingPlanInputs.forEach((input) => {
        input.addEventListener(CustomSubscriptionWidget.EVENTS.CHANGE, () => {
          const parentLabel = input.closest(CustomSubscriptionWidget.SELECTORS.SUBSCRIBE_LABEL);
          const labelInput = parentLabel?.querySelector('input');

          if (labelInput) {
            labelInput.dispatchEvent(new CustomEvent(CustomSubscriptionWidget.EVENTS.CHANGE));
          }
        });
      });
    }

    /**
     * Create URL with appropriate query parameters
     * @param {string} type - Subscription type ('subscription' or 'one-time')
     * @returns {string} - Complete URL with query parameters
     * @private
     */
    createUrl(type) {
      const baseUrl = `${location.origin}/products/${this.productHandle}`;

      const checkedSellingPlan = this.querySelector(`${CustomSubscriptionWidget.SELECTORS.SELLING_PLAN}:checked`);
      const variantInput = this.closest(CustomSubscriptionWidget.SELECTORS.PRODUCT_DETAILS)
        ?.querySelector(CustomSubscriptionWidget.SELECTORS.VARIANT_INPUT);

      const sellingPlanValue = checkedSellingPlan instanceof HTMLInputElement ? checkedSellingPlan.value : '';
      const variantValue = variantInput instanceof HTMLInputElement ? variantInput.value : '';

      /** @type {Record<string, string>} */
      const queryParams = {
        section_id: this.sectionId || '',
        custom_subscription_widget: 'true',
        selling_plan_id: sellingPlanValue,
        variant: variantValue,
      };

      if (type === CustomSubscriptionWidget.SUBSCRIPTION_TYPE.SUBSCRIPTION) {
        queryParams.subscription = 'true';
        queryParams.selling_plan = sellingPlanValue;
      }

      const url = new URL(baseUrl);
      url.search = new URLSearchParams(queryParams).toString();

      return url.href;
    }

    /**
     * Set opacity for all price elements
     * @param {number} opacity - Opacity value (0-1)
     * @private
     */
    setPriceOpacity(opacity) {
      const priceWrappers = document.querySelectorAll(CustomSubscriptionWidget.SELECTORS.PRODUCT_PRICE);
      priceWrappers.forEach((priceTag) => {
        if (priceTag instanceof HTMLElement) {
          priceTag.style.opacity = String(opacity);
        }
      });
    }

    /**
     * Fetch and render updated content
     * @param {string} requestUrl - URL to fetch updated content from
     * @private
     */
    async render(requestUrl) {
      this.classList.add(CustomSubscriptionWidget.CSS_CLASSES.LOADING);

      try {
        const response = await fetch(requestUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        this.updatePriceTags(html);
        this.updateSubscriptionWidget(html);
        this.setPriceOpacity(1);
        this.removeLoadingState();
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        this.handleRenderError();
      }
    }

    /**
     * Update price tags with new content
     * @param {Document} html - Parsed HTML document
     * @private
     */
    updatePriceTags(html) {
      const ajaxPriceTags = html.querySelectorAll(
        `${CustomSubscriptionWidget.SELECTORS.PRODUCT_DETAILS} ${CustomSubscriptionWidget.SELECTORS.PRODUCT_PRICE}`
      );
      const currentPriceTags = this.closest(CustomSubscriptionWidget.SELECTORS.PRODUCT_DETAILS)
        ?.querySelectorAll(CustomSubscriptionWidget.SELECTORS.PRODUCT_PRICE);

      if (ajaxPriceTags && currentPriceTags && ajaxPriceTags.length === currentPriceTags.length) {
        currentPriceTags.forEach((priceTag, index) => {
          const newElement = ajaxPriceTags[index];
          if (newElement) {
            priceTag.replaceWith(newElement);
          }
        });
      }
    }

    /**
     * Update subscription widget with new content
     * @param {Document} html - Parsed HTML document
     * @private
     */
    updateSubscriptionWidget(html) {
      const ajaxSubscriptionWidget = html.querySelector('custom-subscription-widget');
      const currentSubscriptionWidget = this.closest(CustomSubscriptionWidget.SELECTORS.PRODUCT_DETAILS)
        ?.querySelector('custom-subscription-widget');

      if (ajaxSubscriptionWidget && currentSubscriptionWidget) {
        currentSubscriptionWidget.replaceWith(ajaxSubscriptionWidget);
      }
    }

    /**
     * Remove loading state from all price elements
     * @private
     */
    removeLoadingState() {
      const customSubscriptionWidgets = document.querySelectorAll(CustomSubscriptionWidget.SELECTORS.PRODUCT_PRICE);
      customSubscriptionWidgets.forEach((widget) => {
        widget.classList.remove(CustomSubscriptionWidget.CSS_CLASSES.LOADING);
      });
    }

    /**
     * Handle errors during rendering
     * @private
     */
    handleRenderError() {
      this.classList.remove(CustomSubscriptionWidget.CSS_CLASSES.LOADING);
      this.setPriceOpacity(1);
    }
  }

  customElements.define('custom-subscription-widget', CustomSubscriptionWidget);
} 
